
import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';

interface Team {
  name: string;
  birth_date_cutoff?: string | null;
  clubs?: {
    name: string;
    organizations?: {
      name: string;
    } | null;
  } | null;
  seasons?: {
    name: string;
    season_year: number;
    status: 'active' | 'upcoming' | 'inactive';
  } | null;
  divisions?: {
    name: string;
  } | null;
}

interface TeamInfoCardProps {
  team: Team;
  playerCount: number;
}

export const TeamInfoCard = ({ team, playerCount }: TeamInfoCardProps) => {
  const getStatusBadgeColor = (status: 'active' | 'upcoming' | 'inactive' | undefined) => {
    switch (status) {
      case 'active':
        return 'bg-green-100 text-green-800';
      case 'upcoming':
        return 'bg-blue-100 text-blue-800';
      case 'inactive':
        return 'bg-gray-100 text-gray-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusBulletColor = (status: 'active' | 'upcoming' | 'inactive' | undefined) => {
    switch (status) {
      case 'active':
        return 'bg-green-500';
      case 'upcoming':
        return 'bg-blue-500';
      case 'inactive':
        return 'bg-gray-400';
      default:
        return 'bg-gray-400';
    }
  };

  const formatBirthDateCutoff = (birthDateString: string | null) => {
    if (!birthDateString) return 'No cutoff date set';
    
    const [year, month, day] = birthDateString.split('-').map(Number);
    const localDate = new Date(year, month - 1, day);
    
    return `After ${localDate.toLocaleDateString()}`;
  };

  return (
    <Card className="mb-8">
      <CardHeader>
        <CardTitle>Team Information</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <p className="text-sm font-medium text-muted-foreground">Club</p>
            <p className="text-base">{team.clubs?.name || 'No club assigned'}</p>
          </div>
          <div>
            <p className="text-sm font-medium text-muted-foreground">Organization</p>
            <p className="text-base">{team.clubs?.organizations?.name || 'No organization'}</p>
          </div>
          {team.seasons && (
            <div>
              <p className="text-sm font-medium text-muted-foreground">Season</p>
              <div className="flex items-center gap-2">
                <div className={`w-2 h-2 rounded-full ${getStatusBulletColor(team.seasons.status)}`}></div>
                <span className="text-base">{team.seasons.season_year} - {team.seasons.name}</span>
                <span className={`px-2 py-1 text-xs rounded-md ${getStatusBadgeColor(team.seasons.status)}`}>
                  {team.seasons.status?.charAt(0).toUpperCase() + team.seasons.status?.slice(1)}
                </span>
              </div>
            </div>
          )}
          {team.divisions && (
            <div>
              <p className="text-sm font-medium text-muted-foreground">Division</p>
              <p className="text-base">{team.divisions.name}</p>
            </div>
          )}
          <div>
            <p className="text-sm font-medium text-muted-foreground">Birthdate Cutoff</p>
            <p className="text-base">
              {formatBirthDateCutoff(team.birth_date_cutoff)}
            </p>
          </div>
          <div>
            <p className="text-sm font-medium text-muted-foreground">Players</p>
            <p className="text-base">{playerCount}</p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};
